import React, { useEffect, useState } from 'react';
import { settingsApi } from '../utils/api';

type EngineerProfile = {
  skills?: string[];
  baseAddress?: string | null;
  basePostcode?: string | null;
  baseLatitude?: number | null;
  baseLongitude?: number | null;
  serviceRadiusMiles?: number | null;
  maxJobsPerDay?: number | null;
  active?: boolean;
};

type Props = {
  userId: string;
  initial: EngineerProfile;
  onClose: () => void;
  onSaved?: (updated: EngineerProfile) => void;
};

export default function EngineerProfileEditor({ userId, initial, onClose, onSaved }: Props) {
  const [skills, setSkills] = useState<string[]>(initial.skills || []);
  const [skillInput, setSkillInput] = useState('');
  const [baseAddress, setBaseAddress] = useState(initial.baseAddress || '');
  const [basePostcode, setBasePostcode] = useState(initial.basePostcode || '');
  const [serviceRadiusMiles, setServiceRadiusMiles] = useState<string>(
    initial.serviceRadiusMiles != null ? String(initial.serviceRadiusMiles) : '',
  );
  const [maxJobsPerDay, setMaxJobsPerDay] = useState<string>(
    initial.maxJobsPerDay != null ? String(initial.maxJobsPerDay) : '',
  );
  const [active, setActive] = useState<boolean>(initial.active !== false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const addSkill = (raw: string) => {
    const s = String(raw || '').trim();
    if (!s) return;
    setSkills((prev) =>
      prev.map((x) => x.toLowerCase()).includes(s.toLowerCase()) ? prev : [...prev, s],
    );
    setSkillInput('');
  };

  const removeSkill = (skill: string) => {
    setSkills((prev) => prev.filter((s) => s !== skill));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await settingsApi.updateEngineerProfile(userId, {
        skills,
        baseAddress: baseAddress.trim() || null,
        basePostcode: basePostcode.trim() || null,
        serviceRadiusMiles: serviceRadiusMiles ? parseInt(serviceRadiusMiles, 10) : null,
        maxJobsPerDay: maxJobsPerDay ? parseInt(maxJobsPerDay, 10) : null,
        active,
      });
      onSaved?.((res as any).data?.user || (res as any).user);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Engineer profile</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Skills &amp; location help the system assign the right engineer automatically.
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-gray-100 text-gray-500">✕</button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Skills */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Skills</label>
            <p className="text-[11px] text-gray-500 mb-2">
              Free text — use your own terminology. Press Enter to add.
            </p>
            <div className="flex flex-wrap gap-1.5 min-h-[40px] px-2 py-1.5 rounded-lg border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-orange-500 focus-within:border-transparent">
              {skills.map((skill) => (
                <span
                  key={skill}
                  className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full text-xs font-medium"
                >
                  {skill}
                  <button
                    type="button"
                    onClick={() => removeSkill(skill)}
                    className="text-indigo-400 hover:text-indigo-700"
                    aria-label={`Remove ${skill}`}
                  >
                    ×
                  </button>
                </span>
              ))}
              <input
                type="text"
                value={skillInput}
                onChange={(e) => setSkillInput(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ',') && skillInput.trim()) {
                    e.preventDefault();
                    addSkill(skillInput);
                  } else if (e.key === 'Backspace' && !skillInput && skills.length > 0) {
                    removeSkill(skills[skills.length - 1]);
                  }
                }}
                onBlur={() => skillInput.trim() && addSkill(skillInput)}
                className="flex-1 min-w-[140px] text-sm outline-none bg-transparent"
                placeholder={skills.length === 0 ? 'e.g. Gas Safe, EICR, Boiler install' : 'Add another…'}
              />
            </div>
          </div>

          {/* Base location */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Base address</label>
            <input
              type="text"
              value={baseAddress}
              onChange={(e) => setBaseAddress(e.target.value)}
              placeholder="e.g. 12 High Street, London"
              className="h-9 w-full rounded-lg border border-gray-300 px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Base postcode</label>
              <input
                type="text"
                value={basePostcode}
                onChange={(e) => setBasePostcode(e.target.value.toUpperCase())}
                placeholder="SW1A 1AA"
                className="h-9 w-full rounded-lg border border-gray-300 px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent uppercase"
              />
              <p className="text-[11px] text-gray-500 mt-1">Used to compute distance to each job.</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Service radius (miles)</label>
              <input
                type="number"
                min="1"
                value={serviceRadiusMiles}
                onChange={(e) => setServiceRadiusMiles(e.target.value)}
                placeholder="e.g. 25"
                className="h-9 w-full rounded-lg border border-gray-300 px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <p className="text-[11px] text-gray-500 mt-1">Leave blank for no limit.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Max jobs per day</label>
              <input
                type="number"
                min="1"
                value={maxJobsPerDay}
                onChange={(e) => setMaxJobsPerDay(e.target.value)}
                placeholder="Optional"
                className="h-9 w-full rounded-lg border border-gray-300 px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                  className="accent-orange-500"
                />
                <span className="text-sm text-gray-700">Available for assignment</span>
              </label>
            </div>
          </div>

          {error && (
            <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-2">
              {error}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            className="px-4 py-2 text-sm font-semibold text-white bg-orange-500 rounded-md hover:bg-orange-600 disabled:opacity-50"
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save profile'}
          </button>
        </div>
      </div>
    </div>
  );
}
